import { defineStore } from "pinia";

import templatesApi from "@/services/api/templatesApi";
import templateBlocksApi from "@/services/api/templateBlocksApi";
import ordersApi from "@/services/api/ordersApi";
import attributesApi from "@/services/api/attributesApi";

import { getCompanyId } from "@/utils/userUtils";
import { DEFAULT_BLOCK_CONFIGS } from "@/config/template-blocks/blockConfigs";

export const useTemplateStore = defineStore("templateStore", {
  state: () => ({
    templates: [],
    attributes: [],
    itemAttributes: [],
    templateBlocks: [],

    companyId: getCompanyId(),
    isLoading: false,
    error: null,

    templateKindOptions: [
      { value: "order_request", label: "Шаблон заявки" },
      { value: "order_invoice", label: "Шаблон счета" },
      { value: "expeditor_sheet", label: "Шаблон груза" },
    ],

    blockTypeOptions: [
      { value: "text", label: "Текст (Text)" },
      { value: "spacer", label: "Отступ (Spacer)" },
      { value: "table", label: "Таблица (Table)" },
      { value: "items_table", label: "Таблица данных (ItemsTable)" },
      {
        value: "sectioned_items_table",
        label: "Секционная таблица (SectionedItemsTable)",
      },
      { value: "side_by_side", label: "Две колонки (SideBySide)" },
      { value: "signature", label: "Подпись (Signature)" },
      { value: "header", label: "Верхний колонтитул (Header)" },
      { value: "footer", label: "Нижний колонтитул (Footer)" },
    ],

    defaultBlockConfigs: DEFAULT_BLOCK_CONFIGS,
  }),

  getters: {
    templateKindLabels: (state) =>
      Object.fromEntries(
        state.templateKindOptions.map((opt) => [opt.value, opt.label])
      ),

    blockTypeLabels: (state) =>
      Object.fromEntries(
        state.blockTypeOptions.map((opt) => [opt.value, opt.label])
      ),
  },

  actions: {
    formatTemplateKind(kind) {
      return this.templateKindLabels[kind] || "—";
    },

    // --- Templates ---
    async getTemplatesRequest() {
      this.isLoading = true;
      try {
        const response = await templatesApi.index(this.companyId);
        const rawTemplates =
          response.data.result || response.data || [];
        this.templates = rawTemplates;
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении шаблонов:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async showTemplate(id) {
      try {
        const response = await templatesApi.show(this.companyId, id);

        return response.data.result || response.data || {};
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении шаблона:", error);
      }
    },

    async createTemplate(payload) {
      this.isLoading = true;
      try {
        await templatesApi.create(this.companyId, payload);

        await this.getTemplatesRequest();
      } catch (error) {
        console.error("Ошибка при создании шаблона:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateTemplate(id, payload) {
      this.isLoading = true;
      try {
        await templatesApi.update(this.companyId, id, payload);

        await this.getTemplatesRequest();
      } catch (error) {
        console.error("Ошибка при обновлении шаблона:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async templateEnable(id) {
      this.isLoading = true;
      try {
        await templatesApi.enable(this.companyId, id);

        await this.getTemplatesRequest();
      } catch (error) {
        console.error("Ошибка при включении шаблона:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async templateDisable(id) {
      this.isLoading = true;
      try {
        await templatesApi.disable(this.companyId, id);

        await this.getTemplatesRequest();
      } catch (error) {
        console.error("Ошибка при отключении шаблона:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async templateDuplicate(id) {
      this.isLoading = true;
      try {
        await templatesApi.duplicate(this.companyId, id);

        await this.getTemplatesRequest();
      } catch (error) {
        console.error("Ошибка при дублировании шаблона:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async renderTemplateAttributes(params) {
      this.isLoading = true;
      try {
        const response = await attributesApi.renderAttributes(
          this.companyId,
          params
        );

        const rawAttributes =
          response.data.result || response.data || {};

        this.attributes = rawAttributes;
      } catch (error) {
        console.error(
          "Ошибка при получении атрибутов шаблона:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async renderTemplateItemAttributes(params) {
      this.isLoading = true;
      try {
        const response = await attributesApi.renderAttributes(
          this.companyId,
          params
        );

        const rawAttributes =
          response.data.result || response.data || {};

        this.itemAttributes = rawAttributes;
      } catch (error) {
        console.error(
          "Ошибка при получении атрибутов для item:",
          error
        );
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async renderTemplate(id, params) {
      try {
        const response = await templatesApi.renderPdf(
          this.companyId,
          id,
          params
        );

        const blob = new Blob([response.data], {
          type: "application/pdf",
        });
        const url = window.URL.createObjectURL(blob);

        window.open(url, "_blank");

        setTimeout(() => window.URL.revokeObjectURL(url), 100);

        return response;
      } catch (error) {
        console.error("Ошибка рендера файла шаблона:", error);
        throw error;
      }
    },

    async loadPreviewRecord({ companyId }) {
      try {
        const response = await ordersApi.index(companyId, {
          enabled: true,
        });

        const recordId = response.data.result[0].id;

        return recordId;
      } catch (error) {
        console.error(`Не найдена запись для предпросмотра`);
        return null;
      }
    },

    // --- Blocks ---
    async getTemplateBlocksRequest(templateId) {
      try {
        const response = await templateBlocksApi.index(
          this.companyId,
          templateId
        );

        const blocks = response.data.result || response.data || [];

        this.templateBlocks = {
          ...this.templateBlocks,
          [templateId]: blocks,
        };
      } catch (error) {
        console.error("Ошибка при получении блоков:", error);
      }
    },

    async createBlock(templateId, payload) {
      this.isLoading = true;
      try {
        await templateBlocksApi.create(
          this.companyId,
          templateId,
          payload
        );
        await this.getTemplateBlocksRequest(templateId);
      } catch (error) {
        console.error("Ошибка при создании блока:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateBlock(templateId, blockId, payload) {
      this.isLoading = true;
      try {
        await templateBlocksApi.update(
          this.companyId,
          templateId,
          blockId,
          payload
        );
        await this.getTemplateBlocksRequest(templateId);
      } catch (error) {
        console.error("Ошибка при обновлении блока:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async reorderBlocks(templateId, reorderData) {
      this.isLoading = true;
      try {
        await templateBlocksApi.reorder(
          this.companyId,
          templateId,
          reorderData
        );

        await this.getTemplateBlocksRequest(templateId);
      } catch (error) {
        console.error("Ошибка при изменении порядка блоков:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async blockEnable(templateId, blockId) {
      this.isLoading = true;
      try {
        await templateBlocksApi.enable(
          this.companyId,
          templateId,
          blockId
        );

        await this.getTemplateBlocksRequest(templateId);
      } catch (error) {
        console.error("Ошибка при включении блока:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async blockDisable(templateId, blockId) {
      this.isLoading = true;
      try {
        await templateBlocksApi.disable(
          this.companyId,
          templateId,
          blockId
        );

        await this.getTemplateBlocksRequest(templateId);
      } catch (error) {
        console.error("Ошибка при отключении блока:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
